import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Check, Trash2, UserX, Loader2, Calendar, AlertTriangle } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const REASONS = ['Sick', 'Annual leave', 'Personal', 'Family', 'Other'];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function ShiftAlerts() {
  const { token } = useAuth();
  const { selectedStore, storeName } = useStore();
  const storeKey = (selectedStore || 'orewa').toLowerCase();
  const getAuthHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create form
  const [form, setForm] = useState({
    away_staff: '',
    shift_date: todayISO(),
    end_date: '',
    shift_time: '',
    reason: 'Sick',
    notes: '',
    store: storeKey,
  });

  // Resolve dialog state
  const [resolveAlert, setResolveAlert] = useState(null);
  const [coveredBy, setCoveredBy] = useState('');
  const [resolving, setResolving] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/shift-alerts`, { headers: getAuthHeader() });
      setAlerts(res.data || []);
    } catch {
      toast.error('Failed to load shift alerts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    setLoading(true);
    fetchAlerts();
  }, [fetchAlerts]);

  // Reset form whenever store changes (so default store matches)
  useEffect(() => {
    setForm(f => ({ ...f, store: storeKey }));
  }, [storeKey]);

  const handleCreate = async () => {
    if (!form.away_staff.trim()) {
      toast.error('Enter the staff member who is away');
      return;
    }
    if (!form.shift_date) {
      toast.error('Pick the shift date');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/shift-alerts`, form, { headers: getAuthHeader() });
      toast.success('Shift alert posted — it will stay red until cover is assigned');
      setShowCreate(false);
      setForm({
        away_staff: '',
        shift_date: todayISO(),
        end_date: '',
        shift_time: '',
        reason: 'Sick',
        notes: '',
        store: storeKey,
      });
      fetchAlerts();
      // Tell the global banner to refresh
      window.dispatchEvent(new CustomEvent('shift-alerts-changed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create alert');
    } finally {
      setSaving(false);
    }
  };

  const openResolve = (a) => {
    setResolveAlert(a);
    setCoveredBy('');
  };

  const handleResolve = async () => {
    if (!coveredBy.trim()) {
      toast.error('Type the name of the staff member who will cover the shift');
      return;
    }
    setResolving(true);
    try {
      await axios.put(`${API_URL}/shift-alerts/${resolveAlert.id}`, {
        resolved: true,
        covered_by: coveredBy.trim(),
      }, { headers: getAuthHeader() });
      toast.success(`Shift covered by ${coveredBy.trim()}`);
      setResolveAlert(null);
      setCoveredBy('');
      fetchAlerts();
      window.dispatchEvent(new CustomEvent('shift-alerts-changed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resolve');
    } finally {
      setResolving(false);
    }
  };

  const handleReopen = async (a) => {
    try {
      await axios.put(`${API_URL}/shift-alerts/${a.id}`, { resolved: false }, { headers: getAuthHeader() });
      toast.info('Re-opened');
      fetchAlerts();
      window.dispatchEvent(new CustomEvent('shift-alerts-changed'));
    } catch {
      toast.error('Could not re-open');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete shift alert for ${a.away_staff}?`)) return;
    try {
      await axios.delete(`${API_URL}/shift-alerts/${a.id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchAlerts();
      window.dispatchEvent(new CustomEvent('shift-alerts-changed'));
    } catch {
      toast.error('Could not delete');
    }
  };

  const pending = alerts.filter(a => !a.resolved);
  const resolved = alerts.filter(a => a.resolved).slice(0, 5);

  const formatShiftDate = (iso) => {
    if (!iso) return '';
    // Try YYYY-MM-DD first
    const m = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + 'T00:00:00') : null;
    if (m && !Number.isNaN(m.getTime())) return formatDate(m);
    return iso;
  };

  return (
    <section className="space-y-3" data-testid="shift-alerts-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UserX className="w-5 h-5 text-red-600" />
          <h2 className="font-bold text-slate-800 uppercase tracking-wider text-sm">Shift Coverage</h2>
          {pending.length > 0 && (
            <Badge className="bg-red-600 text-white text-xs" data-testid="shift-alerts-pending-badge">
              {pending.length} unresolved
            </Badge>
          )}
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="shift-alert-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Flag Staff Away
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {pending.length === 0 && resolved.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-6 text-center text-slate-400 text-sm">
                No shift alerts. Use <strong>Flag Staff Away</strong> when someone tells you they can't make a shift.
              </CardContent>
            </Card>
          )}

          {/* Pending alerts - solid red treatment */}
          {pending.map((a) => (
            <Card
              key={a.id}
              className="border-2 border-red-500 bg-red-50 shadow-sm"
              data-testid={`shift-alert-pending-${a.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-red-900">
                        {a.away_staff} away — needs cover
                      </p>
                      <p className="text-sm text-red-800 mt-0.5 flex items-center gap-2 flex-wrap">
                        <Calendar className="w-3.5 h-3.5" />
                        <span><strong>{formatShiftDate(a.shift_date)}</strong>
                          {a.end_date ? ` → ${formatShiftDate(a.end_date)}` : ''}
                        </span>
                        {a.shift_time && <span>· {a.shift_time}</span>}
                        {a.store && <Badge variant="outline" className="bg-white text-xs">{storeName(a.store)}</Badge>}
                        {a.reason && <Badge variant="outline" className="bg-white text-xs">{a.reason}</Badge>}
                      </p>
                      {a.notes && <p className="text-xs text-red-700 mt-1 italic">"{a.notes}"</p>}
                      <p className="text-xs text-red-600 mt-1.5">Flagged by {a.created_by}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      onClick={() => openResolve(a)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`shift-alert-resolve-${a.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Assign Cover
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(a)}
                      className="text-red-700 hover:bg-red-100"
                      data-testid={`shift-alert-delete-${a.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Resolved (last 5) */}
          {resolved.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-500 py-1 hover:text-slate-700">
                Resolved ({resolved.length}{alerts.filter(a => a.resolved).length > 5 ? `+` : ''})
              </summary>
              <div className="space-y-2 mt-2">
                {resolved.map((a) => (
                  <Card key={a.id} className="border border-slate-200 bg-slate-50" data-testid={`shift-alert-resolved-${a.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap text-sm">
                        <div className="text-slate-600">
                          <Check className="w-3.5 h-3.5 text-emerald-600 inline mr-1" />
                          <strong>{a.away_staff}</strong> on <strong>{formatShiftDate(a.shift_date)}</strong>
                          {' · '}covered by <strong>{a.covered_by || '—'}</strong>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleReopen(a)}>Reopen</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-red-600" onClick={() => handleDelete(a)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <UserX className="w-5 h-5" /> Flag staff away
            </DialogTitle>
            <DialogDescription>
              This will stay red until you mark it covered. You'll also see a banner on every page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Staff name (the one who is away) *</Label>
              <Input
                value={form.away_staff}
                onChange={(e) => setForm({ ...form, away_staff: e.target.value })}
                placeholder="e.g. Katie"
                data-testid="shift-alert-staff-input"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">From *</Label>
                <Input
                  type="date"
                  value={form.shift_date}
                  onChange={(e) => setForm({ ...form, shift_date: e.target.value })}
                  data-testid="shift-alert-from-date"
                />
              </div>
              <div>
                <Label className="text-xs">To (optional)</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="shift-alert-end-date"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Shift time (optional)</Label>
                <Input
                  value={form.shift_time}
                  onChange={(e) => setForm({ ...form, shift_time: e.target.value })}
                  placeholder="9am–3pm"
                />
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Store</Label>
              <Select value={form.store} onValueChange={(v) => setForm({ ...form, store: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
                  <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. doctor appointment in the morning"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCreate}
              disabled={saving}
              data-testid="shift-alert-save-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Posting…</> : 'Post Alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog open={!!resolveAlert} onOpenChange={(o) => !o && setResolveAlert(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Check className="w-5 h-5" /> Assign cover
            </DialogTitle>
            <DialogDescription>
              Who is covering for <strong>{resolveAlert?.away_staff}</strong> on <strong>{formatShiftDate(resolveAlert?.shift_date)}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Cover staff name *</Label>
            <Input
              value={coveredBy}
              onChange={(e) => setCoveredBy(e.target.value)}
              placeholder="e.g. Sarah"
              onKeyDown={(e) => { if (e.key === 'Enter') handleResolve(); }}
              data-testid="shift-alert-cover-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveAlert(null)}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleResolve}
              disabled={resolving}
              data-testid="shift-alert-confirm-resolve"
            >
              {resolving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Mark Covered'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
